import { createClient } from 'graphql-ws';

import type { HttpOptions } from '@apollo/client';
import { ApolloClient, ApolloProvider, InMemoryCache, createHttpLink, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { canUseDOM } from '@ps/web-core-utils';
import React, { useMemo } from 'react';

interface Props {
  httpLinkUri: HttpOptions['uri'];
  wsLinkUrl?: string;
  token?: string;
  httpHeaders?: Record<string, unknown>;
}

const getApolloClient = (
  httpLinkUri: Props['httpLinkUri'],
  wsLinkUrl: Props['wsLinkUrl'],
  token: Props['token'],
  httpHeaders: Props['httpHeaders'] = {}
) => {
  let link = createHttpLink({ uri: httpLinkUri });
  if (token) {
    const authLink = setContext((_, { headers }) => ({
      headers: { ...headers, ...httpHeaders, authorization: `Bearer ${token}` }
    }));
    link = authLink.concat(link);
  }

  if (canUseDOM && wsLinkUrl) {
    const wsLink = new GraphQLWsLink(
      createClient({
        url: wsLinkUrl,
        keepAlive: 40000,
        connectionParams: async () =>
          token
            ? {
                authorization: `Bearer ${token}`
              }
            : {}
      })
    );

    link = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      wsLink,
      link
    );
  }

  return new ApolloClient({
    link,
    cache: new InMemoryCache()
  });
};

const ApolloClientProvider: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  httpLinkUri,
  wsLinkUrl,
  token,
  httpHeaders
}) => {
  const client = useMemo(
    () => getApolloClient(httpLinkUri, wsLinkUrl, token, httpHeaders),
    [httpLinkUri, wsLinkUrl, token, httpHeaders]
  );

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export { ApolloClientProvider as default, getApolloClient };
